import React, { useEffect } from 'react';
import { Formik } from 'formik';
import { BiConversation } from 'react-icons/bi';

import Field from 'legacy/common/components/wysiwyg-form/Field';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { v4 as uuid } from 'uuid';

enum QuestionType {
  SIMPLE_QUESTION = 'SIMPLE_QUESTION',
  GOOGLE_FORM = 'GOOGLE_FORM'
}

export interface GuestQuestion {
  question: string;
  id: string;
}

type FormValueType = {
  guestQuestions?: GuestQuestion[];
  googleForms?: { url: string };
  questionType?: QuestionType;
};
interface GuestQuestionModalProps {
  initialValues: FormValueType;
  onClose: () => void;
  onSave: (values: {
    guestQuestions?: GuestQuestion[];
    googleForms?: { url: string };
  }) => void;
}

const GuestQuestionModal: React.FC<GuestQuestionModalProps> = ({
  initialValues,
  onClose,
  onSave
}) => {
  const getQuestionType = () => {
    if (initialValues?.guestQuestions?.length > 0) {
      return QuestionType.SIMPLE_QUESTION;
    }

    if (initialValues.googleForms?.url) {
      return QuestionType.GOOGLE_FORM;
    }

    return null;
  };
  const addQuestion = (
    questions: GuestQuestion[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue('guestQuestions', [
      ...questions,
      { question: '', id: uuid() }
    ]);
  };

  const removeQuestion = (
    index: number,
    questions: GuestQuestion[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const newQuestions = [...questions];
    newQuestions.splice(index, 1);
    if (newQuestions.length === 0) {
      newQuestions.push({ question: '', id: uuid() });
    }
    setFieldValue('guestQuestions', newQuestions);
  };

  const handleOnSubmit = (values: FormValueType) => {
    const guestQuestions = values.guestQuestions.filter(
      (val) => val.question !== ''
    );
    onSave({
      guestQuestions,
      googleForms: values.googleForms
    });
    onClose();
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        questionType: getQuestionType()
      }}
      onSubmit={handleOnSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm }) => {
        useEffect(() => {
          if (values?.questionType === QuestionType.SIMPLE_QUESTION) {
            if (
              !values?.guestQuestions ||
              values?.guestQuestions?.length === 0
            ) {
              setFieldValue('guestQuestions', [{ question: '', id: uuid() }]);
            }
          }
        }, [values?.questionType]);

        return (
          <Modal
            type={ModalType.SMALL}
            onClose={onClose}
            autoHeight
            visible
            icon={<BiConversation size={20} />}
            title="Guest question"
            renderFooter={() => (
              <Button type={ButtonType.Primary} onClick={submitForm}>
                Save question
              </Button>
            )}
          >
            <div className="px-4 pt-6">
              <Field
                as="select"
                type="number"
                label="Question type"
                id="questionType"
                name="questionType"
                clearable
              >
                <option value="">Select a question type</option>
                <option value={QuestionType.SIMPLE_QUESTION}>
                  Simple question
                </option>
                <option value={QuestionType.GOOGLE_FORM}>Google form</option>
              </Field>

              {values?.questionType === QuestionType.SIMPLE_QUESTION && (
                <div>
                  {values.guestQuestions?.map((val, index) => (
                    <div key={val.id}>
                      <Field
                        type="text"
                        label="Question"
                        id={`question-title-${index}`}
                        name={`guestQuestions[${index}].question`}
                        placeholder="e.g. What is your favorite brand of beer?"
                      />
                      <div className="mt-2 flex flex-row justify-between items-center">
                        <div className="border-b-1 border-gray-200 w-4/5" />
                        <div
                          className="cursor-pointer text-red-600 text-sm font-medium"
                          onClick={() =>
                            removeQuestion(
                              index,
                              values.guestQuestions,
                              setFieldValue
                            )
                          }
                        >
                          Remove
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="mt-4 text-blue-600 font-normal font-sfprotext text-base leading-5 tracking-tight"
                    onClick={() =>
                      addQuestion(values.guestQuestions, setFieldValue)
                    }
                  >
                    + Add another question
                  </button>
                </div>
              )}

              {values?.questionType === QuestionType.GOOGLE_FORM && (
                <Field
                  type="text"
                  label="URL"
                  id="googleForms.url"
                  name="googleForms.url"
                  placeholder="e.g. https://forms.gle/gju7yqy8PSmdCean6"
                />
              )}
            </div>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default GuestQuestionModal;
